/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { Button, Colors, Img, Typography } from '@zapier/design-system';
import { RichText } from 'components/RichText/RichText';
import { FullWidthCTABody } from 'data/SingleArticle/types';
import { mediaQueries } from 'components/mediaQueries';
import { ScrollTracking, TrackedComponent, useAvroTracking } from 'tracking';
import { getLinkTarget } from 'utils/getLinkTarget';

export type Props = {
  heading?: string;
  subheading?: string;
  body?: FullWidthCTABody;
  buttonText?: string;
  buttonUrl?: string;
  imageUrl?: string;
  imageAltText?: string;
  imageSize?: 'small' | 'large';
};

const Styles = labelStyles('EmbeddedFullWidthCTA', {
  root: (imageSize: Props['imageSize']) => [
    css({
      width: '100%',
      margin: '20px auto',
      padding: '20px',
      backgroundColor: Colors.peach,
      borderRadius: '5px',

      [mediaQueries.tablet]: {
        padding: '24px',
        margin: '40px auto',
      },
    }),
    imageSize === 'small' &&
      withModifier(
        'smallImage',
        css({
          [mediaQueries.tablet]: {
            display: 'flex',
            flexDirection: 'row-reverse',
            alignItems: 'center',
          },
        }),
      ),
  ],

  image: (imageSize: Props['imageSize']) => [
    css({
      maxWidth: '100%',
      marginBottom: '16px',

      [mediaQueries.tablet]: {
        marginBottom: '24px',
      },
    }),
    imageSize === 'small' &&
      withModifier(
        'smallImage',
        css({
          [mediaQueries.tablet]: {
            marginBottom: '0',
          },
        }),
      ),
  ],

  content: (imageSize: Props['imageSize']) => [
    css({
      textAlign: 'center',
    }),
    imageSize === 'small' &&
      withModifier(
        'smallImage',
        css({
          [mediaQueries.tablet]: {
            width: '60%',
            marginRight: '5%',
            flexShrink: 0,
            textAlign: 'left',
          },
        }),
      ),
  ],

  heading: css({
    ...Typography.sectionHeader3Bold,
    color: Colors.neutral800,

    [mediaQueries.tablet]: {
      ...Typography.sectionHeader1Bold,
    },
  }),

  subheading: css({
    ...Typography.paragraph1Short,
    color: Colors.neutral800,
    marginTop: '8px',

    [mediaQueries.tablet]: {
      ...Typography.subHeader1,
      marginTop: '12px',
    },
  }),

  button: css({
    marginTop: '24px',

    [mediaQueries.tablet]: {
      marginTop: '32px',
    },
  }),
});

export const EmbeddedFullWidthCTA = (props: Props) => {
  const { emitCTAClickEvent } = useAvroTracking();
  return (
    <ScrollTracking avroIdentifier={TrackedComponent.FullWidthCTA}>
      <aside css={Styles.root(props.imageSize)} data-testid="full-width-cta">
        {props.imageUrl && props.imageAltText && (
          <div css={Styles.image(props.imageSize)}>
            <Img alt={props.imageAltText} isBlock={true} src={props.imageUrl} />
          </div>
        )}
        <div css={Styles.content(props.imageSize)}>
          {props.heading && <div css={Styles.heading}>{props.heading}</div>}
          {props.subheading && (
            <div css={Styles.subheading}>{props.subheading}</div>
          )}
          {props.body && (
            <RichText
              additionalWrapperStyles={{
                marginTop: '8px',

                [mediaQueries.tablet]: {
                  marginTop: '12px',
                },

                '> *': {
                  marginBottom: '0px',
                  marginTop: '0px',
                },
              }}
              body={props.body}
              persona={null}
            />
          )}
          {props.buttonUrl && props.buttonText && (
            <div css={Styles.button}>
              <Button
                href={props.buttonUrl}
                target={getLinkTarget(props.buttonUrl)}
                color="primary-alt"
                onClick={() => {
                  emitCTAClickEvent({
                    interaction_raw_text: props.buttonText,
                    destination_url_address: props.buttonUrl,
                    custom_tracking_fields: {
                      component: TrackedComponent.FullWidthCTA,
                      heading: props.heading,
                      subheading: props.subheading,
                    },
                  });
                }}
              >
                {props.buttonText}
              </Button>
            </div>
          )}
        </div>
      </aside>
    </ScrollTracking>
  );
};
